import { buildCanonicalUrl } from "./siteSeo.js";

interface AlternativeDef {
  competitorName: string;
  slug: string;
  competitorWeaknesses: string[];
  ourStrengths: string[];
  bestToolPath: string;
  bestToolName: string;
}

const ALTERNATIVES: AlternativeDef[] = [
  {
    competitorName: "Smallpdf",
    slug: "smallpdf-alternative",
    competitorWeaknesses: ["Strict daily limits on free tier", "Requires file uploads to their servers", "Subscription required for unlimited use"],
    ourStrengths: ["100% Free with no daily limits", "Processes files locally in your browser", "No server uploads required"],
    bestToolPath: "/tools",
    bestToolName: "All PDF Tools"
  },
  {
    competitorName: "Adobe Acrobat",
    slug: "adobe-acrobat-alternative",
    competitorWeaknesses: ["Expensive monthly subscriptions", "Heavy, slow desktop application", "Complex interface for simple tasks"],
    ourStrengths: ["Free, no-subscription model", "Runs instantly in any modern web browser", "Simple, focused tools for single tasks"],
    bestToolPath: "/tools",
    bestToolName: "All PDF Tools"
  },
  {
    competitorName: "iLovePDF",
    slug: "ilovepdf-alternative",
    competitorWeaknesses: ["Files must be uploaded to remote servers", "Ads on the free tier", "File size limits for non-paying users"],
    ourStrengths: ["Absolute privacy: files never leave your device", "Ad-free experience", "Uses your device memory, no hard server limits"],
    bestToolPath: "/tools/merge",
    bestToolName: "Merge PDF"
  },
  {
    competitorName: "Sejda",
    slug: "sejda-alternative",
    competitorWeaknesses: ["Page and file size limits on free tier", "3 tasks per hour limit", "Files uploaded to servers"],
    ourStrengths: ["Unlimited tasks per hour", "No strict page count limits (depends on your device)", "True client-side processing"],
    bestToolPath: "/tools/edit-pdf",
    bestToolName: "Edit PDF"
  },
  {
    competitorName: "PDF24",
    slug: "pdf24-alternative",
    competitorWeaknesses: ["Outdated interface", "Cloud tools require uploads", "Desktop app requires installation"],
    ourStrengths: ["Modern, fast user interface", "Zero installation required", "Local processing without the desktop app overhead"],
    bestToolPath: "/tools",
    bestToolName: "All PDF Tools"
  }
];

export interface AlternativePage {
  slug: string;
  path: string;
  title: string;
  metaDescription: string;
  keywords: string;
  h1: string;
  competitorName: string;
  competitorWeaknesses: string[];
  ourStrengths: string[];
  bestToolPath: string;
  bestToolName: string;
  paragraphs: string[];
  faqs: { question: string; answer: string }[];
  jsonLd: Record<string, unknown>[];
}

function buildAlternativePage(def: AlternativeDef): AlternativePage {
  const path = `/alternatives/${def.slug}`;
  const h1 = `The Best Free ${def.competitorName} Alternative`;
  const title = `${h1} | No Limits, Private, Fast`;
  const metaDescription = `Looking for a free alternative to ${def.competitorName}? PDFWorkSpace runs entirely in your browser with no daily limits, no subscriptions, and absolute privacy.`;
  const keywords = `${def.competitorName.toLowerCase()} alternative, free ${def.competitorName.toLowerCase()} alternative, better than ${def.competitorName.toLowerCase()}, private pdf tools, local pdf editor`;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: h1,
      description: metaDescription,
      url: buildCanonicalUrl(path),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: `Is PDFWorkSpace a free alternative to ${def.competitorName}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `Yes, PDFWorkSpace is a completely free alternative to ${def.competitorName}. Unlike ${def.competitorName}, which has paywalls and limits, PDFWorkSpace offers unlimited usage.`
          }
        },
        {
          "@type": "Question",
          name: `How is PDFWorkSpace more private than ${def.competitorName}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `PDFWorkSpace processes all files locally in your web browser. Your PDFs are never uploaded to a remote server, ensuring absolute privacy. ${def.competitorName} typically requires file uploads.`
          }
        }
      ]
    }
  ];

  return {
    ...def,
    path,
    title,
    metaDescription,
    keywords,
    h1,
    jsonLd,
    paragraphs: [
      `If you are tired of hitting paywalls, daily limits, or upload restrictions with ${def.competitorName}, you've found the right place. PDFWorkSpace is engineered from the ground up to be the most private and unrestricted PDF toolkit available.`,
      `The biggest difference is our architecture. ${def.competitorName} typically requires you to upload your sensitive documents to their servers. We use WebAssembly and modern browser APIs to process everything directly on your device. No uploads, no waiting in server queues, and no risk of your files being compromised.`
    ],
    faqs: jsonLd[1].mainEntity.map((item: any) => ({
      question: item.name,
      answer: item.acceptedAnswer.text
    }))
  };
}

export const ALTERNATIVE_SEO_PAGES = ALTERNATIVES.map(buildAlternativePage);

export const ALTERNATIVE_SEO_MAP = new Map<string, AlternativePage>(
  ALTERNATIVE_SEO_PAGES.map((page) => [page.slug, page])
);

export const ALTERNATIVE_SEO_PATHS = ALTERNATIVE_SEO_PAGES.map((page) => page.path);
